import { z } from "zod";
import { defineStepConfig, VisibilityPredicateType } from "../../../registry";
import { IntegrationKey } from "@erp/shared-types";
import {
  FAMOUS_SQL_QUERY_DEFAULT_TIMEOUT_SECONDS,
  FAMOUS_SQL_QUERY_MAX_ROWS,
  FAMOUS_SQL_QUERY_MAX_TIMEOUT_SECONDS,
  FAMOUS_SQL_QUERY_MIN_TIMEOUT_SECONDS,
  FAMOUS_SQL_QUERY_STEP_TYPE,
  famousSqlQueryDetectedColumnsSchema,
  famousSqlQueryParametersSchema,
} from "../../../utils/famousSqlQuerySockets";

/**
 * Famous SQL Query Step
 *
 * Runs a user-authored, read-only SQL query against the Famous Oracle
 * database. Useful for migrating Famous ingest flows into user-editable
 * workflows and for ad-hoc Famous data access from a workflow.
 *
 * - SQL is validated to be a single read-only SELECT (also enforced against
 *   Oracle statement metadata at execution time).
 * - Bind parameters declared in config become typed input sockets; the SQL
 *   references them as `:name`.
 * - Output sockets are typed from column metadata detected by the editor's
 *   "Run preview" action (stored in config as `detectedColumns`), alongside
 *   the always-present `rows` and `rowCount` sockets.
 * - Results are capped at 10,000 rows.
 */
export const famousSqlQueryStep = defineStepConfig({
  type: FAMOUS_SQL_QUERY_STEP_TYPE,
  name: "Famous SQL Query",
  description:
    "Run a read-only SQL query against the Famous Oracle database and output the result rows",
  category: "Data Access",
  libraryModule: "Famous",

  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],

  // Sockets are dynamic (declared bind parameters / detected columns); the
  // generic record schemas are the runtime fallback.
  inputSchema: z.record(z.string(), z.unknown()),
  outputSchema: z.record(z.string(), z.unknown()),

  configSchema: z.object({
    sql: z
      .string()
      .max(50_000)
      .default("")
      .describe("Read-only SELECT statement to run against Famous"),
    parameters: famousSqlQueryParametersSchema
      .default([])
      .describe(
        "Bind parameters referenced by the SQL as :name; each becomes a typed input socket",
      ),
    maxRows: z
      .number()
      .int()
      .min(1)
      .max(FAMOUS_SQL_QUERY_MAX_ROWS)
      .default(FAMOUS_SQL_QUERY_MAX_ROWS)
      .describe("Maximum number of rows to return"),
    queryTimeoutSeconds: z
      .number()
      .int()
      .min(FAMOUS_SQL_QUERY_MIN_TIMEOUT_SECONDS)
      .max(FAMOUS_SQL_QUERY_MAX_TIMEOUT_SECONDS)
      .default(FAMOUS_SQL_QUERY_DEFAULT_TIMEOUT_SECONDS)
      .describe(
        "Maximum seconds for each Oracle round-trip before the query is cancelled",
      ),
    detectedColumns: famousSqlQueryDetectedColumnsSchema
      .default([])
      .describe(
        "Result columns detected by the preview action; each becomes a typed output socket",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 1 },
  },
});
